import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API_URL as API } from '../config/env.js';

const NewsDataContext = createContext(null);

const EMPTY_FEED = {
  breaking: [],
  topStories: [],
  editionLive: false,
  latestPaperDate: null,
  announcement: { active: false },
  waTemplates: null,
};

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) return null;
  return res.json();
}

function mergeFeed(feed) {
  if (!feed || typeof feed !== 'object') return null;
  return {
    breaking: feed.breaking || [],
    topStories: feed.topStories || [],
    editionLive: !!feed.editionLive,
    latestPaperDate: feed.latestPaperDate || null,
    announcement: feed.announcement || { active: false },
    waTemplates: feed.waTemplates || null,
  };
}

export function NewsDataProvider({ children }) {
  const [stories, setStories] = useState([]);
  const [homeFeed, setHomeFeed] = useState(EMPTY_FEED);
  const [loading, setLoading] = useState(true);
  const [feedError, setFeedError] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setFeedError(false);

    let gotStories = false;
    let gotFeed = false;

    const paintEarly = () => {
      // Unblock Exclusive Stories as soon as either payload arrives.
      if (gotStories || gotFeed) setLoading(false);
    };

    const storiesPromise = fetchJson(`${API}/api/stories`)
      .then((raw) => {
        if (raw === null) return;
        const list = Array.isArray(raw) ? raw : [];
        setStories(list);
        gotStories = true;
        paintEarly();
      })
      .catch(() => {});

    const feedPromise = fetchJson(`${API}/api/home-feed`)
      .then(async (feed) => {
        const merged = mergeFeed(feed);
        if (merged) {
          setHomeFeed(merged);
          // Instant first paint from topStories while full /api/stories is still loading.
          setStories((prev) => (prev.length ? prev : merged.topStories || []));
          gotFeed = true;
          paintEarly();
          return;
        }

        // Fallback if home-feed fails — keep parallel, not sequential.
        const [breaking, papers] = await Promise.all([
          fetchJson(`${API}/api/breaking-news`),
          fetchJson(`${API}/api/papers`),
        ]);
        let latestPaperDate = null;
        if (papers?.dates?.length) latestPaperDate = papers.dates[0];
        const today = new Date().toISOString().slice(0, 10);
        setHomeFeed({
          ...EMPTY_FEED,
          breaking: Array.isArray(breaking) ? breaking : [],
          latestPaperDate,
          editionLive: !!(latestPaperDate && latestPaperDate === today),
        });
        gotFeed = breaking !== null || papers !== null;
        paintEarly();
      })
      .catch(() => {});

    await Promise.all([storiesPromise, feedPromise]);

    setStories((prev) => {
      if (prev.length) return prev;
      return [];
    });
    setFeedError(!gotStories && !gotFeed);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getStoryById = useCallback(
    (id) => stories.find((s) => s.id === id) || homeFeed.topStories.find((s) => s.id === id),
    [stories, homeFeed.topStories]
  );

  return (
    <NewsDataContext.Provider value={{ stories, homeFeed, loading, feedError, refresh, getStoryById }}>
      {children}
    </NewsDataContext.Provider>
  );
}

export function useNewsData() {
  const ctx = useContext(NewsDataContext);
  if (!ctx) throw new Error('useNewsData must be used within NewsDataProvider');
  return ctx;
}
